/**
 * Takeovers dashboard types — mirrors backend/src/types/takeovers.ts and
 * backend/src/types/clientReports.ts (the admin-facing subset).
 */

export type TakeoverRequestStatus = 'new' | 'in_review' | 'approved' | 'booked' | 'declined' | 'expired';

export const TAKEOVER_STATUSES: TakeoverRequestStatus[] = [
  'new', 'in_review', 'approved', 'booked', 'declined', 'expired',
];

export const STATUS_LABELS: Record<TakeoverRequestStatus, string> = {
  new: 'New',
  in_review: 'In Review',
  approved: 'Approved',
  booked: 'Booked',
  declined: 'Declined',
  expired: 'Expired',
};

/** Which statuses each status may move to (mirrors the server transition map). */
export const STATUS_NEXT: Record<TakeoverRequestStatus, TakeoverRequestStatus[]> = {
  new: ['in_review', 'declined'],
  in_review: ['approved', 'declined'],
  approved: ['booked', 'declined'],
  booked: ['declined'],
  declined: [],
  expired: [],
};

export interface TakeoverBudgetTier {
  id: string;
  label: string;
  minUsd?: number;
  maxUsd?: number;
  sortOrder: number;
}

export interface TakeoverCreatorContact {
  name: string;
  email: string;
  notify: boolean;
}

/** Linked YouTube channel snapshot (set by the editor's channel picker). */
export interface TakeoverCreatorYouTube {
  channelId: string;
  handle?: string;
  subscriberCount?: number;
  syncedAt: string;
}

export interface TakeoverCreator {
  id: string;
  slug: string;
  previousSlugs: string[];
  displayName: string;
  avatarUrl?: string;
  bannerUrl?: string;
  blurb?: string;
  subsDisplay?: string;
  youtube?: TakeoverCreatorYouTube | null;
  active: boolean;
  contacts: TakeoverCreatorContact[];
  budgetTiers: TakeoverBudgetTier[];
  createdAt: string;
  updatedAt: string;
  publicUrl: string;
}

/** Row in the editor's channel picker (CRM + YouTube merged). */
export interface TakeoverChannelCandidate {
  channelId: string;
  title: string;
  thumbnailUrl?: string;
  subsDisplay?: string;
  inNetwork: boolean;
}

/** Fresh identity payload used to auto-fill the creator form. */
export interface TakeoverChannelDetails {
  channelId: string;
  title: string;
  handle?: string;
  subscriberCount?: number;
  subsDisplay?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  channelUrl: string;
}

export interface TakeoverRequest {
  id: string;
  creatorId: string;
  creatorSlug: string;
  creatorDisplayName: string;
  status: TakeoverRequestStatus;
  displayStatus: TakeoverRequestStatus;
  brand: { name: string; website: string };
  contact: { name: string; email: string; phone?: string };
  budgetTierId: string;
  budgetTierLabel: string;
  window: { startDate: string; endDate: string };
  notes?: string;
  briefLink?: string;
  utm?: Partial<Record<'source' | 'medium' | 'campaign' | 'term' | 'content' | 'referrer', string>>;
  statusHistory: Array<{ from: TakeoverRequestStatus; to: TakeoverRequestStatus; at: string; byEmail: string; note?: string }>;
  internalNotes: Array<{ id: string; at: string; byEmail: string; text: string }>;
  emailStatus: { advertiser: string; creator: string; internal: string };
  meta: { ip?: string; userAgent?: string; turnstileVerified: boolean };
  createdAt: string;
  updatedAt: string;
}

export interface OverlappingApproved {
  id: string;
  brand: string;
  status: TakeoverRequestStatus;
  window: { startDate: string; endDate: string };
}

export interface TakeoverAnalyticsSummary {
  totalRequests: number;
  statusCounts: Record<TakeoverRequestStatus, number>;
  requestsPerCreator: Array<{
    creatorId: string;
    creatorDisplayName: string;
    total: number;
    statusCounts: Partial<Record<TakeoverRequestStatus, number>>;
  }>;
  budgetTierCounts: Array<{ label: string; count: number }>;
  pipelineEstimateUsd: number;
}
